/**
 * CanvasPanel — Main canvas container.
 * Renders the tab bar, version history, and the active tab's editor.
 */

import { FileText, PanelRightClose } from 'lucide-react'
import { CanvasTabs } from './CanvasTabs'
import { CanvasEditor } from './CanvasEditor'
import { VersionHistory } from './VersionHistory'
import { Button } from '../ui/button'
import api from '../../services/api'
import type { UseCanvasReturn } from '../../hooks/useCanvas'

interface CanvasPanelProps {
  canvas: UseCanvasReturn
  isGenerating?: boolean
  onCollapse?: () => void
  lastTurnMeta?: { latencyMs: number; totalTokens?: number }
}

export function CanvasPanel({ canvas, isGenerating = false, onCollapse, lastTurnMeta }: CanvasPanelProps) {
  const {
    tabs,
    activeTab,
    versions,
    suggestion,
    setActiveTab,
    updateTabContent,
    saveToArtifact,
    acceptHunk,
    rejectHunk,
    acceptAll,
    rejectAll,
    restoreVersion,
  } = canvas

  return (
    <div className="flex flex-col h-full border-l border-border bg-background">
      {/* Tab bar with icon, draft badge, version history, and collapse */}
      <div className="flex items-center gap-1 px-2 py-0.5 border-b border-border/50">
        <FileText className="h-3.5 w-3.5 text-primary flex-shrink-0" />

        <CanvasTabs activeTab={activeTab} tabs={tabs} onTabChange={setActiveTab} />

        {/* DRAFT badge when active tab has content */}
        {tabs[activeTab]?.content && (
          <span className="text-[9px] font-medium text-ai-amber-text bg-ai-amber-soft px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0">
            Draft
          </span>
        )}

        <div className="flex-1" />

        <VersionHistory tab={activeTab} versions={versions[activeTab]} onRestore={restoreVersion} />

        {onCollapse && (
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground flex-shrink-0" onClick={onCollapse} title="Collapse clinical note">
            <PanelRightClose className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Active tab editor */}
      <div className="flex-1 overflow-hidden">
        <CanvasEditor
          tab={activeTab}
          tabState={tabs[activeTab]}
          suggestion={suggestion}
          isGenerating={isGenerating}
          lastTurnMeta={lastTurnMeta}
          onContentChange={updateTabContent}
          onSave={saveToArtifact}
          onAcceptHunk={acceptHunk}
          onRejectHunk={rejectHunk}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          // Rebuild intentionally not wired: feature pulled behind
          // CASE_PANEL_REBUILD_ENABLED (backend, default OFF) — one-shot regen
          // degrades pipeline-built notes. #105 spec has the re-enable path.
          onFormat={async (tab) => {
            const { data } = await api.post('/api/v1/case-panel/format', {
              content: tabs[tab].content,
              tab_type: tab,
            })
            if (data.formatted_content) {
              updateTabContent(tab, data.formatted_content)
            }
          }}
        />
      </div>
    </div>
  )
}
